import points from "./points";
import helper from "./helper";
/**
 * @typedef KanitzGradientFill
 * @type {object}
 * @property {string} from
 * @property {string} to
 */

/**
 * @typedef KanitzChartOptions
 * @type {object}
 * @property {number} scale
 * @property {HTMLCanvasElement} canvas
 * @property {number[]} range
 * @property {number} value
 * @property {number} gridSteps
 * @property {number[]} negativeColor
 * @property {number[]} positiveColor
 * @property {number} animationIterations
 * @property {bool} responsible
 * @property {string} fontStyle
 * @property {KanitzGradientFill} gradientBackground
 * @property {CanvasGradient} canvasGradient
 */

export default class Kanitz {
  /**
   * @param {KanitzChartOptions} options
   */
  constructor(options) {
    if (!options || !options.canvas) {
      throw "canvas is required";
    }
    /**
     * @type {HTMLCanvasElement} this.canvas
     */


    this.canvas = options.canvas;
    this.context = this.canvas.getContext("2d");

    if (!this.context) {
      throw 'cannot get 2d context from canvas';
    }

    options.responsible = typeof options.responsible === "boolean" ? options.responsible : true;
    options.fontStyle = options.fontStyle || "32px Segoe UI";
    options.fontColor = options.fontColor || "#555";
    options.borderColor = options.borderColor || "#eaeaea";
    options.backgroundColor = options.backgroundColor || "transparent";
    options.animationIterations = options.animationIterations || 60;
    options.negativeColor = options.negativeColor || [255, 0, 0, 1];
    options.positiveColor = options.positiveColor || [0, 164, 255, 1];
    options.gridSteps = options.gridSteps || 7;
    options.scale = options.scale || 150;
    options.range = options.range || [-7, 7];

    if (options.range[0] - options.range[1] == 0) {
      throw "invalid range";
    }

    options.value = options.value || 0;
    this.options = options;

    if (this.options.gradientBackground) {
      this.options.canvasGradient = this._createGradient(this.options);
    }

    this.validateValueRange();

    if (options.responsible && window.ResizeObserver) {
      new ResizeObserver(() => {
        this.canvas.width = this.canvas.clientWidth * 2;
        this.canvas.height = this.canvas.clientHeight * 2;
        this.drawImplementation();
      }).observe(this.canvas);
    }
  }
  /**
   * 
   * @param {KanitzChartOptions} options 
   */


  _createGradient(options) {
    const height = this.canvas.height;
    const canvasGradient = this.context.createLinearGradient(0, -this.getUpperPosition(), 0, -this.getLowerPosition());
    canvasGradient.addColorStop(0, options.gradientBackground.from);
    canvasGradient.addColorStop(1, options.gradientBackground.to);
    return canvasGradient;
  }
  /**
   * @param {number} value
   */


  set value(value) {
    this.options.value = value || 0;
    this.validateValueRange();
    this.draw();
  }

  validateValueRange() {
    if (this.options.value < this.options.range[0] || this.options.value > this.options.range[1]) {
      throw `value ${this.options.value} out of range ${this.options.range[0]}..${this.options.range[1]}`;
    }
  }

  drawImplementation() {
    // clear
    const context = this.context;
    const _this$canvas = this.canvas,
          width = _this$canvas.width,
          height = _this$canvas.height;
    context.clearRect(0, 0, width, height); // fill value

    this._fillValue(); // mask


    this._drawMaskPart(-1);

    this._drawMaskPart(1); // borders


    this._drawBorder(-1);

    this._drawBorder(1); // bars and legend


    this._drawBarsAndLegend();
  }

  draw() {
    if (this.smoothOrigin == null) {
      this.smoothOrigin = this.options.range[0];
    }

    this.drawStep = 0;

    const callback = () => {
      this.drawImplementation();
      this.drawStep++;

      if (this.drawStep <= this.options.animationIterations) {
        window.requestAnimationFrame(callback);
      } else {
        // end of animation
        // update smooth origin
        this.smoothOrigin = this.options.value;
      }
    };

    window.requestAnimationFrame(callback);
  } // internal


  _fillValue() {
    const _this$canvas2 = this.canvas,
          width = _this$canvas2.width,
          height = _this$canvas2.height;
    const _this$options = this.options,
          range = _this$options.range,
          value = _this$options.value,
          negativeColor = _this$options.negativeColor,
          positiveColor = _this$options.positiveColor;
    /**
     * @type {CanvasRenderingContext2D} context
     */

    const context = this.context;
    const bottom = this.getLowerPosition();
    const top = this.getUpperPosition();
    const interpolation = helper.bezierBlend(this.drawStep / this.options.animationIterations);
    const targetHeightInterpolation = (value - range[0]) / (range[1] - range[0]);
    const targetHeight = bottom + (top - bottom) * targetHeightInterpolation;
    const originHeightInterpolation = (this.smoothOrigin - range[0]) / (range[1] - range[0]);
    const originHeight = bottom + (top - bottom) * originHeightInterpolation;
    const resultHeight = originHeight + (targetHeight - originHeight) * interpolation;
    context.moveTo(0, height);

    if (this.options.canvasGradient) {
      this.context.fillStyle = this.options.canvasGradient;
    } else {
      const colorInterpolation = originHeightInterpolation + (targetHeightInterpolation - originHeightInterpolation) * interpolation; // create color to interpolate

      const c = [Math.floor(negativeColor[0] + (positiveColor[0] - negativeColor[0]) * colorInterpolation), Math.floor(negativeColor[1] + (positiveColor[1] - negativeColor[1]) * colorInterpolation), Math.floor(negativeColor[2] + (positiveColor[2] - negativeColor[2]) * colorInterpolation), Math.floor(negativeColor[3] + (positiveColor[3] - negativeColor[3]) * colorInterpolation)];
      this.context.fillStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${c[3]})`;
    }

    context.fillRect(0, resultHeight, width, height - resultHeight);
  }

  _buildSidePath(side) {
    const _this$canvas3 = this.canvas,
          width = _this$canvas3.width,
          height = _this$canvas3.height;
    const context = this.context;
    const scale = this.options.scale;
    const mid = width / 2;
    context.arc(mid, height + points[2][1] * scale, Math.abs(points[0][0] * scale), Math.PI * 1.5, Math.PI * 0.5 + Math.PI * 0.5 * -side, -side > 0);
    context.lineTo(mid + points[1][0] * scale * -side, height + points[1][1] * scale);
    context.arc(mid, height + points[3][1] * scale, helper.distance(mid + points[1][0] * scale, height + points[1][1] * scale, mid, height + points[3][1] * scale), Math.PI * 1.5 + side * 0.17 * Math.PI, Math.PI * 0.5, -side > 0);
  }

  _drawMaskPart(side) {
    const _this$canvas4 = this.canvas,
          width = _this$canvas4.width,
          height = _this$canvas4.height;
    const context = this.context;
    const scale = this.options.scale;
    const mid = width / 2;
    const border = mid + mid * side;
    context.beginPath();
    context.moveTo(border, 0);
    context.lineTo(mid, 0);
    context.lineTo(mid, height + points[2][1] * scale);

    this._buildSidePath(side);

    context.lineTo(mid, height);
    context.lineTo(border, height);
    context.closePath();
    context.fillStyle = this.options.backgroundColor;
    context.fill();
  }

  _drawBorder(side) {
    /**
     * @type {CanvasRenderingContext2D} context
     */
    const context = this.context;
    const _this$canvas5 = this.canvas,
          width = _this$canvas5.width,
          height = _this$canvas5.height;
    const mid = width / 2;
    const scale = this.options.scale;
    context.beginPath();
    context.strokeStyle = this.options.borderColor;
    context.moveTo(mid, height + points[2][1] * scale + points[0][0] * scale);

    this._buildSidePath(side);

    context.lineWidth = 5;
    context.stroke();
  }
  /**
   * @returns {number}
   */


  getLowerPosition() {
    const height = this.canvas.height;
    const scale = this.options.scale;
    return height + points[3][1] * scale + 25;
  }
  /**
   * @returns {number}
   */


  getUpperPosition() {
    const height = this.canvas.height;
    const scale = this.options.scale;
    return height + points[2][1] * scale;
  }

  _drawBarsAndLegend() {
    const context = this.context;
    const lower = this.getLowerPosition();
    const upper = this.getUpperPosition();
    const _this$options2 = this.options,
          steps = _this$options2.gridSteps,
          scale = _this$options2.scale,
          range = _this$options2.range;
    const width = this.canvas.width;
    const mid = width / 2;
    const barWidth = 0.065;
    context.strokeStyle = "#ddd";

    for (let x = 0; x <= steps; x++) {
      context.beginPath(); // interpolate height

      const height = lower + (upper - lower) * x / steps;
      context.moveTo(mid - barWidth * scale, height);
      context.lineTo(mid + barWidth * scale, height);
      context.stroke();
      context.fillStyle = this.options.fontColor;
      context.font = this.options.fontStyle;
      const legend = Math.floor(range[0] + (range[1] - range[0]) * x / steps);
      context.fillText(legend.toString(), mid + 1.1 * scale, height + 8);
    }
  }

}